import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from './api';
import ModuleQuickCreate from './ModuleQuickCreate';
import UnifiedCourseImport from './UnifiedCourseImport';
import useAdminConfirm from './useAdminConfirm';
import { generateHtmlFromChapitre } from '../utils/courseHtml';

const PAGE_SIZE = 30;

const ACCESS_OPTIONS = [
  ['free', 'Free'],
  ['premium', 'Premium'],
];

const STATUS_OPTIONS = [
  ['', 'Tous les états'],
  ['draft', 'Brouillon'],
  ['published', 'Publié'],
  ['archived', 'Archivé'],
];

const STATUS_LABELS = {
  draft: 'Brouillon',
  published: 'Publié',
  archived: 'Archivé',
};

function StatusBadge({ status }) {
  return (
    <span className={`bo-badge bo-badge--${status || 'draft'}`}>
      {STATUS_LABELS[status] || status || '—'}
    </span>
  );
}

function AccessBadge({ tier }) {
  return (
    <span
      className={`bo-badge ${
        tier === 'premium' ? 'bo-badge--premium' : 'bo-badge--free'
      }`}
    >
      {tier === 'premium' ? 'Premium' : 'Free'}
    </span>
  );
}

function SortButton({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <button className="bo-sort-button" onClick={() => onSort(sortKey)}>
      {label}
      <span
        className={`bo-sort-indicator ${
          active ? 'bo-sort-indicator--active' : ''
        }`}
      >
        {active ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString('fr-FR')
    : '—';
}

function arrayToText(value) {
  return Array.isArray(value) ? value.join('\n') : '';
}

function textToArray(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function emptyCourseForm() {
  return {
    module_id: '',
    titre: '',
    access_tier: 'free',
    objectif_global: '',
    principe_editorial: '',
  };
}

function emptyChapterForm() {
  return {
    titre: '',
    objectif: '',
    ordre: 1,
  };
}

function emptySectionForm() {
  return {
    titre: '',
    objectif_pedagogique: '',
    contenu: '',
    notions_cles: '',
    a_retenir: '',
    vigilances: '',
    statut_fiabilite: '',
    ordre: 1,
  };
}

function courseEditorFromStructure(course) {
  return {
    module_id: course?.module_id ? String(course.module_id) : '',
    titre: course?.titre || '',
    objectif_global: course?.objectif_global || '',
    principe_editorial: course?.principe_editorial || '',
  };
}

function sectionEditorFromSection(section) {
  return {
    titre: section?.titre || '',
    objectif_pedagogique: section?.objectif_pedagogique || '',
    contenu: section?.contenu || '',
    notions_cles: arrayToText(section?.notions_cles),
    a_retenir: arrayToText(section?.a_retenir),
    vigilances: arrayToText(section?.vigilances),
    statut_fiabilite: section?.statut_fiabilite || '',
    ordre: section?.ordre || 1,
  };
}

export default function CourseManager({
  catalog,
  refresh,
  runAction,
  onUnauthorized,
}) {
  const modules = catalog?.modules || [];
  const activeModules = modules.filter((m) => m.is_active !== false);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { askConfirm, confirmDialog } = useAdminConfirm();
  const [reordering, setReordering] = useState(false);
  const draggingRef = useRef(null);

  const [courseForm, setCourseForm] = useState(emptyCourseForm);
  const [filters, setFilters] = useState({
    search: '',
    moduleId: '',
    access: '',
    status: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [sort, setSort] = useState({ key: 'updated_at', direction: 'desc' });
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState({ items: [], total: 0 });
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [structure, setStructure] = useState(null);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [courseEditor, setCourseEditor] = useState(emptyCourseForm);
  const [chapterForm, setChapterForm] = useState(emptyChapterForm);
  const [creatingSectionFor, setCreatingSectionFor] = useState(null);
  const [sectionDrafts, setSectionDrafts] = useState({});
  const [editingChapterId, setEditingChapterId] = useState(null);
  const [chapterEdit, setChapterEdit] = useState(emptyChapterForm);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [sectionEdit, setSectionEdit] = useState(emptySectionForm);
  const [viewMode, setViewMode] = useState('structure');
  const [justCreatedId, setJustCreatedId] = useState(null);
  const [error, setError] = useState('');
  const gridRef = useRef(null);
  const detailRef = useRef(null);
  const sectionFormRef = useRef(null);
  const pendingScrollRef = useRef(false);

  const loadList = useCallback(
    async (requestedOffset = offset) => {
      try {
        setLoadingList(true);
        setError('');
        const next = await adminApi.courses({
          ...appliedFilters,
          sort: sort.key,
          order: sort.direction,
          limit: PAGE_SIZE,
          offset: requestedOffset,
        });
        setResult(next || { items: [], total: 0 });

        // Do not auto-select a course in the catalogue. In particular, do
        // not replace a newly created course with a different paginated row.
      } catch (loadError) {
        if (loadError.status === 401) onUnauthorized?.();
        else setError(loadError.message || 'Chargement des cours impossible.');
      } finally {
        setLoadingList(false);
      }
    },
    [
      appliedFilters,
      offset,
      onUnauthorized,
      sort.direction,
      sort.key,
    ]
  );

  const loadStructure = useCallback(
    async (id) => {
      if (!id) {
        setStructure(null);
        return;
      }
      try {
        setLoadingStructure(true);
        setError('');
        const next = await adminApi.courseStructure(id);
        setStructure(next);
        setCourseEditor(courseEditorFromStructure(next));
        setEditingChapterId(null);
        setEditingSectionId(null);
        setCreatingSectionFor(null);

        if (pendingScrollRef.current && detailRef.current?.scrollIntoView) {
          pendingScrollRef.current = false;
          requestAnimationFrame(() => {
            detailRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          });
        }
      } catch (loadError) {
        if (loadError.status === 401) onUnauthorized?.();
        else setError(loadError.message || 'Chargement du cours impossible.');
      } finally {
        setLoadingStructure(false);
      }
    },
    [onUnauthorized]
  );

  useEffect(() => {
    loadList(offset);
  }, [offset, appliedFilters, sort.key, sort.direction]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadStructure(selectedId);
  }, [selectedId, loadStructure]);

  useEffect(() => {
    if (!creatingSectionFor || viewMode !== 'structure') return undefined;
    const handle = requestAnimationFrame(() => {
      sectionFormRef.current?.scrollIntoView?.({
        behavior: 'smooth', block: 'start',
      });
    });
    return () => cancelAnimationFrame(handle);
  }, [creatingSectionFor, viewMode]);

  const isDraft = structure?.publication_status === 'draft';
  const chapterCount = structure?.chapters?.length || 0;
  const sectionCount = (structure?.chapters || []).reduce(
    (sum, chapter) => sum + (chapter.sections || []).length,
    0
  );

  async function reorderItems(type, parentId, items, sourceId, targetId) {
    if (!isDraft || reordering || sourceId === targetId) return;
    const sourceIndex = items.findIndex((item) => item.id === sourceId);
    const targetIndex = items.findIndex((item) => item.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const order = items.map((item) => item.id);
    order.splice(sourceIndex, 1);
    order.splice(targetIndex, 0, sourceId);
    setReordering(true);
    try {
      const ok = await runAction(
        () => type === 'chapter'
          ? adminApi.reorderCourseChapters(parentId, order)
          : adminApi.reorderChapterSections(parentId, order),
        'Ordre enregistré.'
      );
      if (ok) await Promise.all([loadStructure(structure.id), loadList(offset)]);
    } finally {
      setReordering(false);
      draggingRef.current = null;
    }
  }

  function moveItem(type, parentId, items, id, delta) {
    const index = items.findIndex((item) => item.id === id);
    const neighbor = items[index + delta];
    if (neighbor) reorderItems(type, parentId, items, id, neighbor.id);
  }

  function startDrag(event, type, parentId, id) {
    if (!isDraft || reordering) {
      event.preventDefault();
      return;
    }
    draggingRef.current = { type, parentId, id };
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(id));
  }

  function acceptDrag(event, type, parentId) {
    const drag = draggingRef.current;
    if (!drag || drag.type !== type || drag.parentId !== parentId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  async function dropItem(event, type, parentId, items, targetId) {
    const drag = draggingRef.current;
    if (!drag || drag.type !== type || drag.parentId !== parentId) return;
    event.preventDefault();
    await reorderItems(type, parentId, items, drag.id, targetId);
  }

  function applyFilters(event) {
    event?.preventDefault?.();
    setAppliedFilters({ ...filters });
    setOffset(0);
  }

  function resetFilters() {
    const next = { search: '', moduleId: '', access: '', status: '' };
    setFilters(next);
    setAppliedFilters(next);
    setOffset(0);
  }

  function changeSort(key) {
    setSort((current) => ({
      key,
      direction:
        current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setOffset(0);
  }

  function openCourse(id, scroll = true) {
    pendingScrollRef.current = scroll;
    setJustCreatedId(null);
    setViewMode('structure');
    setSelectedId(id);
    if (Number(id) === Number(selectedId)) loadStructure(id);
  }

  function returnToCatalog() {
    pendingScrollRef.current = false;
    setSelectedId(null);
    setStructure(null);
    setJustCreatedId(null);
    setViewMode('structure');
    setCreatingSectionFor(null);
    setEditingChapterId(null);
    setEditingSectionId(null);
    requestAnimationFrame(() => {
      gridRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
  }

  async function createCourse(event) {
    event.preventDefault();
    let created = null;
    const ok = await runAction(
      async () => {
        created = await adminApi.createCourse({
          module_id: Number(courseForm.module_id),
          titre: courseForm.titre,
          objectif_global: courseForm.objectif_global || null,
          principe_editorial: courseForm.principe_editorial || null,
          access_tier: courseForm.access_tier,
        });
      },
      'Cours créé en brouillon.'
    );

    if (!ok || !created) return;
    setCourseForm(emptyCourseForm());
    setFormOpen(false);
    setFilters({ search: '', moduleId: '', access: '', status: '' });
    setAppliedFilters({ search: '', moduleId: '', access: '', status: '' });
    setOffset(0);
    setViewMode('structure');
    setJustCreatedId(created.id);
    pendingScrollRef.current = true;
    setSelectedId(created.id);
    await Promise.all([loadList(0), refresh?.()]);
  }

  async function saveCourse(event) {
    event.preventDefault();
    if (!structure || !isDraft) return;

    const ok = await runAction(
      () =>
        adminApi.updateCourse(structure.id, {
          module_id: Number(courseEditor.module_id),
          titre: courseEditor.titre,
          objectif_global: courseEditor.objectif_global || null,
          principe_editorial: courseEditor.principe_editorial || null,
        }),
      'Cours mis à jour.'
    );
    if (!ok) return;
    await Promise.all([
      loadStructure(structure.id),
      loadList(offset),
      refresh?.(),
    ]);
  }

  async function updateAccess(value) {
    if (!structure) return;
    if (
      structure.publication_status === 'published' &&
      !(await askConfirm({
        title: 'Modifier l’accès du cours ?',
        description: `Le cours est déjà publié. Son accès passera immédiatement à ${value === 'premium' ? 'Premium' : 'Free'}.`,
        confirmLabel: 'Modifier l’accès',
      }))
    ) return;

    const ok = await runAction(
      () => adminApi.updateCourse(structure.id, { access_tier: value }),
      'Accès du cours mis à jour.'
    );
    if (!ok) return;
    await Promise.all([
      loadStructure(structure.id),
      loadList(offset),
      refresh?.(),
    ]);
  }

  async function changePublication(nextStatus) {
    if (!structure) return;

    if (nextStatus === 'published' && !(await askConfirm({
      title: 'Publier ce cours ?',
      description: 'Il sera visible dans Ressources pour les candidats autorisés.',
      confirmLabel: 'Publier le cours',
    }))) return;
    if (nextStatus === 'archived' && !(await askConfirm({
      title: 'Archiver ce cours ?',
      description: 'Il sera retiré du catalogue candidat, sans suppression de son contenu.',
      confirmLabel: 'Archiver le cours',
      intent: 'danger',
    }))) return;

    const ok = await runAction(
      () =>
        adminApi.updateCourse(structure.id, {
          publication_status: nextStatus,
        }),
      nextStatus === 'published'
        ? 'Cours publié.'
        : nextStatus === 'archived'
        ? 'Cours archivé.'
        : 'Cours remis en brouillon.'
    );
    if (!ok) return;

    await Promise.all([
      loadStructure(structure.id),
      loadList(offset),
      refresh?.(),
    ]);
  }

  async function createChapter(event) {
    event.preventDefault();
    if (!structure || !isDraft) return;

    let created = null;
    const ok = await runAction(
      async () => {
        created = await adminApi.createChapter(structure.id, {
          titre: chapterForm.titre,
          objectif: chapterForm.objectif || null,
          ordre: Number(chapterForm.ordre) || chapterCount + 1,
        });
      },
      'Chapitre ajouté. Ajoutez maintenant sa première section.'
    );
    if (!ok) return;
    setChapterForm({
      ...emptyChapterForm(),
      ordre: chapterCount + 2,
    });
    await Promise.all([loadStructure(structure.id), loadList(offset)]);
    if (created?.id) {
      setCreatingSectionFor(created.id);
    }
  }

  function startEditChapter(chapter) {
    setEditingChapterId(chapter.id);
    setChapterEdit({
      titre: chapter.titre || '',
      objectif: chapter.objectif || '',
      ordre: chapter.ordre || 1,
    });
  }

  async function saveChapter(event, chapter) {
    event.preventDefault();
    if (!isDraft) return;
    const ok = await runAction(
      () =>
        adminApi.updateChapter(chapter.id, {
          titre: chapterEdit.titre,
          objectif: chapterEdit.objectif || null,
          ordre: Number(chapterEdit.ordre) || 1,
        }),
      'Chapitre mis à jour.'
    );
    if (!ok) return;
    setEditingChapterId(null);
    await loadStructure(structure.id);
  }

  async function deleteChapter(chapter) {
    if (!isDraft || !(await askConfirm({
      title: 'Supprimer ce chapitre ?',
      description: `Le chapitre « ${chapter.titre} » et toutes ses sections seront supprimés.`,
      confirmLabel: 'Supprimer le chapitre',
      intent: 'danger',
    }))) return;
    const ok = await runAction(
      () => adminApi.deleteChapter(chapter.id),
      'Chapitre supprimé.'
    );
    if (ok) {
      await Promise.all([loadStructure(structure.id), loadList(offset)]);
    }
  }

  function getSectionDraft(chapterId, nextOrder) {
    return (
      sectionDrafts[chapterId] || {
        ...emptySectionForm(),
        ordre: nextOrder,
      }
    );
  }

  function setSectionDraft(chapterId, patch, nextOrder) {
    setSectionDrafts((previous) => ({
      ...previous,
      [chapterId]: {
        ...getSectionDraft(chapterId, nextOrder),
        ...patch,
      },
    }));
  }

  async function createSection(event, chapter) {
    event.preventDefault();
    if (!isDraft) return;
    const draft = getSectionDraft(
      chapter.id,
      (chapter.sections || []).length + 1
    );

    const ok = await runAction(
      () =>
        adminApi.createSection(chapter.id, {
          titre: draft.titre,
          objectif_pedagogique: draft.objectif_pedagogique || null,
          contenu: draft.contenu,
          notions_cles: textToArray(draft.notions_cles),
          a_retenir: textToArray(draft.a_retenir),
          vigilances: textToArray(draft.vigilances),
          statut_fiabilite: draft.statut_fiabilite || null,
          ordre: Number(draft.ordre) || (chapter.sections || []).length + 1,
        }),
      'Section ajoutée.'
    );
    if (!ok) return;

    setSectionDrafts((previous) => {
      const next = { ...previous };
      delete next[chapter.id];
      return next;
    });
    setCreatingSectionFor(null);
    await Promise.all([loadStructure(structure.id), loadList(offset)]);
  }

  function startEditSection(section) {
    setEditingSectionId(section.id);
    setSectionEdit(sectionEditorFromSection(section));
  }

  async function saveSection(event, section) {
    event.preventDefault();
    if (!isDraft) return;

    const ok = await runAction(
      () =>
        adminApi.updateSection(section.id, {
          titre: sectionEdit.titre,
          objectif_pedagogique: sectionEdit.objectif_pedagogique || null,
          contenu: sectionEdit.contenu,
          notions_cles: textToArray(sectionEdit.notions_cles),
          a_retenir: textToArray(sectionEdit.a_retenir),
          vigilances: textToArray(sectionEdit.vigilances),
          statut_fiabilite: sectionEdit.statut_fiabilite || null,
          ordre: Number(sectionEdit.ordre) || 1,
        }),
      'Section mise à jour.'
    );
    if (!ok) return;
    setEditingSectionId(null);
    await loadStructure(structure.id);
  }

  async function deleteSection(section) {
    if (!isDraft || !(await askConfirm({
      title: 'Supprimer cette section ?',
      description: `La section « ${section.titre} » sera supprimée de ce chapitre.`,
      confirmLabel: 'Supprimer la section',
      intent: 'danger',
    }))) return;
    const ok = await runAction(
      () => adminApi.deleteSection(section.id),
      'Section supprimée.'
    );
    if (ok) {
      await Promise.all([loadStructure(structure.id), loadList(offset)]);
    }
  }

  function renderSectionFields(value, setValue, { creation = false } = {}) {
    return (
      <>
        <label>
          Titre
          <input
            className="bo-input"
            value={value.titre}
            onChange={(event) => setValue({ titre: event.target.value })}
            required
          />
        </label>
        <label>
          Position
          <input
            className="bo-input"
            type="number"
            min="1"
            value={value.ordre}
            onChange={(event) => setValue({ ordre: event.target.value })}
          />
        </label>
        <label className="bo-span-2">
          Objectif pédagogique
          <input
            className="bo-input"
            value={value.objectif_pedagogique}
            onChange={(event) =>
              setValue({ objectif_pedagogique: event.target.value })
            }
          />
        </label>
        <label className="bo-span-2">
          Contenu
          <textarea
            className="bo-input"
            rows="8"
            value={value.contenu}
            onChange={(event) => setValue({ contenu: event.target.value })}
            required
          />
        </label>
        <label>
          Notions clés — une par ligne
          <textarea
            className="bo-input"
            rows="4"
            value={value.notions_cles}
            onChange={(event) => setValue({ notions_cles: event.target.value })}
          />
        </label>
        <label>
          À retenir — un élément par ligne
          <textarea
            className="bo-input"
            rows="4"
            value={value.a_retenir}
            onChange={(event) => setValue({ a_retenir: event.target.value })}
          />
        </label>
        <label>
          Vigilances — une par ligne
          <textarea
            className="bo-input"
            rows="4"
            value={value.vigilances}
            onChange={(event) => setValue({ vigilances: event.target.value })}
          />
        </label>
        <label>
          Statut de fiabilité (interne)
          <input
            className="bo-input"
            value={value.statut_fiabilite}
            onChange={(event) =>
              setValue({ statut_fiabilite: event.target.value })
            }
            placeholder={creation ? 'Ex. vérifié' : ''}
          />
        </label>
      </>
    );
  }

  return (
    <div className="bo-stack">
      {confirmDialog}
      <header className="bo-panel__header bo-course-page-header">
        <div>
          <p className="bo-eyebrow">Contenus</p>
          <h2>Cours</h2>
          <p className="bo-muted">
            Cours structurés en chapitres et sections, publiés côté candidat dans
            Ressources.
          </p>
        </div>
        <div className="bo-actions">
          {selectedId ? (
            <button className="bo-button bo-button--ghost" onClick={returnToCatalog}>
              Retour au catalogue
            </button>
          ) : (
            <>
          <button className="bo-button bo-button--ghost" onClick={() => setImportOpen((open) => !open)}>
            {importOpen ? 'Fermer import' : 'Importer des cours'}
          </button>
          <button
            className="bo-button bo-button--primary"
            onClick={() => setFormOpen((current) => !current)}
          >
            {formOpen ? 'Fermer' : '+ Nouveau cours'}
          </button>
            </>
          )}
        </div>
      </header>

      {error && <div className="bo-alert bo-alert--error">{error}</div>}

      {!selectedId && (<>
      {importOpen && (
        <UnifiedCourseImport modules={activeModules}
          onUnauthorized={onUnauthorized}
          onCatalogChanged={refresh}
          onImported={async () => {
            const clean = { search: '', moduleId: '', access: '', status: '' };
            setFilters(clean);
            setAppliedFilters(clean);
            setOffset(0);
            await Promise.all([loadList(0), refresh?.()]);
          }}/>
      )}

      {formOpen && (
        <section className="bo-panel">
          <div className="bo-panel__header">
            <div>
              <h3>Nouveau cours</h3>
              <p className="bo-muted">
                Le cours est créé en brouillon avant l’ajout des chapitres et
                sections.
              </p>
            </div>
            <StatusBadge status="draft" />
          </div>

          <form className="bo-form bo-course-form" onSubmit={createCourse}>
            <label>
              Matière
              <select
                className="bo-input"
                value={courseForm.module_id}
                onChange={(event) =>
                  setCourseForm((current) => ({
                    ...current,
                    module_id: event.target.value,
                  }))
                }
                required
              >
                <option value="">Choisir…</option>
                {activeModules.map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.matiere}
                  </option>
                ))}
              </select>
            </label>
            <div className="bo-span-2">
              <ModuleQuickCreate onCreated={async (module) => {
                setCourseForm((current) => ({ ...current, module_id: String(module.id) }));
                await refresh?.();
              }} />
            </div>
            <label>
              Titre
              <input
                className="bo-input"
                value={courseForm.titre}
                onChange={(event) =>
                  setCourseForm((current) => ({
                    ...current,
                    titre: event.target.value,
                  }))
                }
                required
              />
            </label>
            <label>
              Accès
              <select
                className="bo-input"
                value={courseForm.access_tier}
                onChange={(event) =>
                  setCourseForm((current) => ({
                    ...current,
                    access_tier: event.target.value,
                  }))
                }
              >
                {ACCESS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="bo-span-2">
              Objectif global
              <textarea
                className="bo-input"
                rows="3"
                value={courseForm.objectif_global}
                onChange={(event) =>
                  setCourseForm((current) => ({
                    ...current,
                    objectif_global: event.target.value,
                  }))
                }
              />
            </label>
            <label className="bo-span-2">
              Principe éditorial (interne)
              <textarea
                className="bo-input"
                rows="3"
                value={courseForm.principe_editorial}
                onChange={(event) =>
                  setCourseForm((current) => ({
                    ...current,
                    principe_editorial: event.target.value,
                  }))
                }
                placeholder="Consignes internes de rédaction, niveau attendu, sources…"
              />
            </label>
            <div className="bo-actions bo-span-2">
              <button
                type="button"
                className="bo-button bo-button--ghost"
                onClick={() => {
                  setCourseForm(emptyCourseForm());
                  setFormOpen(false);
                }}
              >
                Annuler
              </button>
              <button className="bo-button bo-button--primary">
                Créer en brouillon
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="bo-panel" ref={gridRef}>
        <div className="bo-panel__header">
          <div>
            <h3>Catalogue des cours</h3>
            <p className="bo-muted">
              {result.total || 0} cours correspondant aux critères.
            </p>
          </div>
          <span className="bo-kpi">
            {loadingList ? 'Chargement…' : `${result.total || 0} résultat(s)`}
          </span>
        </div>

        <form className="bo-course-filters" onSubmit={applyFilters}>
          <label>
            Recherche
            <input
              className="bo-input"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              placeholder="Titre, objectif, matière…"
            />
          </label>
          <label>
            Matière
            <select
              className="bo-input"
              value={filters.moduleId}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  moduleId: event.target.value,
                }))
              }
            >
              <option value="">Toutes</option>
              {activeModules.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.matiere}
                </option>
              ))}
            </select>
          </label>
          <label>
            Accès
            <select
              className="bo-input"
              value={filters.access}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  access: event.target.value,
                }))
              }
            >
              <option value="">Tous</option>
              {ACCESS_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Publication
            <select
              className="bo-input"
              value={filters.status}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  status: event.target.value,
                }))
              }
            >
              {STATUS_OPTIONS.map(([value, label]) => (
                <option key={value || 'all'} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <div className="bo-actions bo-course-filter-actions">
            <button className="bo-button bo-button--primary">Rechercher</button>
            <button
              type="button"
              className="bo-button bo-button--ghost"
              onClick={resetFilters}
            >
              Réinitialiser
            </button>
          </div>
        </form>

        {loadingList && !(result.items || []).length ? (
          <div className="bo-loading">Chargement…</div>
        ) : !(result.items || []).length ? (
          <div className="bo-empty">Aucun cours.</div>
        ) : (
          <div className="bo-table-wrap bo-course-grid-wrap">
            <table className="bo-table bo-course-grid">
              <thead>
                <tr>
                  <th>
                    <SortButton
                      label="Cours"
                      sortKey="title"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>
                    <SortButton
                      label="Matière"
                      sortKey="module"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>
                    <SortButton
                      label="Accès"
                      sortKey="access"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>
                    <SortButton
                      label="État"
                      sortKey="status"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>
                    <SortButton
                      label="Chapitres"
                      sortKey="chapters"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>
                    <SortButton
                      label="Sections"
                      sortKey="sections"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>
                    <SortButton
                      label="Mise à jour"
                      sortKey="updated_at"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {(result.items || []).map((course) => {
                  const selected =
                    Number(course.id) === Number(selectedId);
                  return (
                    <tr
                      key={course.id}
                      className={selected ? 'is-selected' : ''}
                      tabIndex="0"
                      aria-selected={selected}
                      onClick={() => openCourse(course.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openCourse(course.id);
                        }
                      }}
                    >
                      <td>
                        <strong>{course.titre}</strong>
                        <small className="bo-cell-ellipsis">
                          {course.objectif_global || 'Aucun objectif global'}
                        </small>
                      </td>
                      <td>{course.module_title}</td>
                      <td>
                        <AccessBadge tier={course.access_tier} />
                      </td>
                      <td>
                        <StatusBadge status={course.publication_status} />
                      </td>
                      <td className="bo-cell-number">
                        {Number(course.chapter_count || 0)}
                      </td>
                      <td className="bo-cell-number">
                        {Number(course.section_count || 0)}
                      </td>
                      <td>{formatDate(course.updated_at)}</td>
                      <td>
                        <button
                          className="bo-button bo-button--ghost bo-button--compact"
                          onClick={(event) => {
                            event.stopPropagation();
                            openCourse(course.id);
                          }}
                        >
                          Ouvrir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="bo-row bo-row--between bo-pagination">
          <small className="bo-muted">
            {result.total
              ? `${offset + 1}–${Math.min(
                  offset + (result.items || []).length,
                  result.total
                )} sur ${result.total}`
              : '0 cours'}
          </small>
          <div className="bo-actions">
            <button
              className="bo-button bo-button--ghost"
              disabled={loadingList || offset === 0}
              onClick={() =>
                setOffset((current) => Math.max(0, current - PAGE_SIZE))
              }
            >
              Précédent
            </button>
            <button
              className="bo-button bo-button--ghost"
              disabled={
                loadingList ||
                offset + (result.items || []).length >= (result.total || 0)
              }
              onClick={() => setOffset((current) => current + PAGE_SIZE)}
            >
              Suivant
            </button>
          </div>
        </div>
      </section>
      </>)}

      {selectedId && (
      <section className="bo-panel bo-course-detail" ref={detailRef}>
        {!selectedId ? (
          <div className="bo-empty">
            Sélectionnez un cours pour ouvrir sa fiche éditoriale.
          </div>
        ) : loadingStructure && !structure ? (
          <div className="bo-loading">Chargement du cours…</div>
        ) : structure ? (
          <div className="bo-stack">
            <div className="bo-panel__header">
              <div>
                <p className="bo-eyebrow">Cours #{structure.id}</p>
                <h3>{structure.titre}</h3>
                <p className="bo-muted">
                  {structure.module_title} · {chapterCount} chapitre(s) ·{' '}
                  {sectionCount} section(s)
                </p>
              </div>
              <div className="bo-inline-controls">
                <StatusBadge status={structure.publication_status} />
                <select
                  className="bo-input bo-select"
                  value={structure.access_tier}
                  onChange={(event) => updateAccess(event.target.value)}
                >
                  {ACCESS_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bo-tabs bo-course-tabs">
              <button
                className={viewMode === 'structure' ? 'is-active' : ''}
                onClick={() => setViewMode('structure')}
              >
                Structure pédagogique
              </button>
              <button
                className={viewMode === 'information' ? 'is-active' : ''}
                onClick={() => setViewMode('information')}
              >
                Informations du cours
              </button>
              <button
                className={viewMode === 'preview' ? 'is-active' : ''}
                onClick={() => setViewMode('preview')}
              >
                Prévisualisation
              </button>
            </div>

            <div className="bo-course-lifecycle">
              <div>
                <strong>Cycle éditorial</strong>
                <p className="bo-muted">
                  Le contenu et la structure ne sont modifiables qu’en brouillon.
                </p>
              </div>
              <div className="bo-actions">
                {structure.publication_status === 'draft' && (
                  <>
                    <button
                      className="bo-button bo-button--primary"
                      onClick={() => changePublication('published')}
                    >
                      Publier
                    </button>
                    <button
                      className="bo-button bo-button--danger-ghost"
                      onClick={() => changePublication('archived')}
                    >
                      Archiver
                    </button>
                  </>
                )}
                {structure.publication_status === 'published' && (
                  <>
                    <button
                      className="bo-button bo-button--ghost"
                      onClick={() => changePublication('draft')}
                    >
                      Repasser en brouillon
                    </button>
                    <button
                      className="bo-button bo-button--danger-ghost"
                      onClick={() => changePublication('archived')}
                    >
                      Archiver
                    </button>
                  </>
                )}
                {structure.publication_status === 'archived' && (
                  <button
                    className="bo-button bo-button--primary"
                    onClick={() => changePublication('draft')}
                  >
                    Restaurer en brouillon
                  </button>
                )}
              </div>
            </div>

            {viewMode === 'preview' ? (
              <div className="bo-course-preview">
                <div className="bo-course-preview__hero">
                  <span>{structure.module_title}</span>
                  <h2>{structure.titre}</h2>
                  {structure.objectif_global && (
                    <p>{structure.objectif_global}</p>
                  )}
                </div>

                {(structure.chapters || []).length ? (
                  (structure.chapters || []).map((chapter) => (
                    <article className="bo-course-preview__chapter" key={chapter.id}>
                      <div
                        className="bo-course-shared-content"
                        dangerouslySetInnerHTML={{
                          __html: generateHtmlFromChapitre(chapter),
                        }}
                      />
                    </article>
                  ))
                ) : (
                  <div className="bo-empty">Aucun chapitre à prévisualiser.</div>
                )}
              </div>
            ) : (
              <>
                {viewMode === 'information' && (
                <section className="bo-course-meta-card">
                  <div className="bo-panel__header">
                    <div>
                      <h4>Informations du cours</h4>
                      <p className="bo-muted">
                        Métadonnées générales visibles ou utilisées pour
                        l’édition.
                      </p>
                    </div>
                  </div>

                  <form className="bo-form bo-course-form bo-course-info-form" onSubmit={saveCourse}>
                    <div className="bo-course-matter-field">
                      <label>
                      Matière
                      <select
                        className="bo-input"
                        value={courseEditor.module_id}
                        disabled={!isDraft}
                        onChange={(event) =>
                          setCourseEditor((current) => ({
                            ...current,
                            module_id: event.target.value,
                          }))
                        }
                        required
                      >
                        <option value="">Choisir…</option>
                        {activeModules.map((module) => (
                          <option key={module.id} value={module.id}>
                            {module.matiere}
                          </option>
                        ))}
                        {structure?.module_id &&
                          !activeModules.some((m) => m.id === structure.module_id) && (
                            <option value={structure.module_id}>
                              {structure.module_title || 'Matière désactivée'} (désactivée)
                            </option>
                          )}
                      </select>
                      </label>
                      {isDraft && <ModuleQuickCreate
                        onCreated={async (module) => {
                          setCourseEditor((current) => ({ ...current, module_id: String(module.id) }));
                          await refresh?.();
                        }}
                      />}
                    </div>
                    <label>
                      Titre
                      <input
                        className="bo-input"
                        value={courseEditor.titre}
                        disabled={!isDraft}
                        onChange={(event) =>
                          setCourseEditor((current) => ({
                            ...current,
                            titre: event.target.value,
                          }))
                        }
                        required
                      />
                    </label>
                    <label>
                      Objectif global
                      <textarea
                        className="bo-input"
                        rows="3"
                        value={courseEditor.objectif_global}
                        disabled={!isDraft}
                        onChange={(event) =>
                          setCourseEditor((current) => ({
                            ...current,
                            objectif_global: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      Principe éditorial (interne)
                      <textarea
                        className="bo-input"
                        rows="3"
                        value={courseEditor.principe_editorial}
                        disabled={!isDraft}
                        onChange={(event) =>
                          setCourseEditor((current) => ({
                            ...current,
                            principe_editorial: event.target.value,
                          }))
                        }
                      />
                    </label>
                    {isDraft ? (
                      <div className="bo-actions bo-span-2">
                        <button className="bo-button bo-button--primary">
                          Enregistrer les informations
                        </button>
                      </div>
                    ) : (
                      <div className="bo-alert bo-span-2">
                        Repassez ce cours en brouillon pour modifier ses
                        informations et sa structure.
                      </div>
                    )}
                  </form>
                </section>
                )}

                {viewMode === 'structure' && (
                <section className="bo-course-structure">
                  {Number(justCreatedId) === Number(structure.id) && (
                    <div className="bo-course-next-step" role="status">
                      <strong>Cours créé en brouillon.</strong>
                      <p>Ajoutez un chapitre, puis ses sections pour construire ce cours.</p>
                    </div>
                  )}
                  <div className="bo-panel__header">
                    <div>
                      <h4>Structure pédagogique</h4>
                      <p className="bo-muted">
                        {chapterCount} chapitre(s) · {sectionCount} section(s)
                      </p>
                    </div>
                  </div>

                  {isDraft && (
                    <form
                      className="bo-form bo-course-new-chapter"
                      onSubmit={createChapter}
                    >
                      <h5 className="bo-span-2">Ajouter un chapitre</h5>
                      <label>
                        Titre
                        <input
                          className="bo-input"
                          value={chapterForm.titre}
                          onChange={(event) =>
                            setChapterForm((current) => ({
                              ...current,
                              titre: event.target.value,
                            }))
                          }
                          required
                        />
                      </label>
                      <label>
                        Position
                        <input
                          className="bo-input"
                          type="number"
                          min="1"
                          value={chapterForm.ordre}
                          onChange={(event) =>
                            setChapterForm((current) => ({
                              ...current,
                              ordre: event.target.value,
                            }))
                          }
                        />
                      </label>
                      <label className="bo-span-2">
                        Objectif
                        <input
                          className="bo-input"
                          value={chapterForm.objectif}
                          onChange={(event) =>
                            setChapterForm((current) => ({
                              ...current,
                              objectif: event.target.value,
                            }))
                          }
                        />
                      </label>
                      <div className="bo-actions bo-span-2">
                        <button className="bo-button bo-button--secondary">
                          Ajouter le chapitre
                        </button>
                      </div>
                    </form>
                  )}

                  {(structure.chapters || []).length === 0 ? (
                    <div className="bo-empty">
                      Aucun chapitre. Ajoutez le premier chapitre en brouillon.
                    </div>
                  ) : (
                    <div className="bo-course-chapter-list">
                      {(structure.chapters || []).map((chapter) => (
                        <article className="bo-course-chapter" key={chapter.id}
                            onDragOver={(event) => acceptDrag(event, 'chapter', structure.id)}
                            onDrop={(event) => dropItem(event, 'chapter', structure.id, structure.chapters || [], chapter.id)}
                          >
                          {editingChapterId === chapter.id ? (
                            <form
                              className="bo-form bo-course-inline-editor"
                              onSubmit={(event) => saveChapter(event, chapter)}
                            >
                              <label>
                                Titre du chapitre
                                <input
                                  className="bo-input"
                                  value={chapterEdit.titre}
                                  onChange={(event) =>
                                    setChapterEdit((current) => ({
                                      ...current,
                                      titre: event.target.value,
                                    }))
                                  }
                                  required
                                />
                              </label>
                              <label>
                                Position
                                <input
                                  className="bo-input"
                                  type="number"
                                  min="1"
                                  value={chapterEdit.ordre}
                                  onChange={(event) =>
                                    setChapterEdit((current) => ({
                                      ...current,
                                      ordre: event.target.value,
                                    }))
                                  }
                                />
                              </label>
                              <label className="bo-span-2">
                                Objectif
                                <input
                                  className="bo-input"
                                  value={chapterEdit.objectif}
                                  onChange={(event) =>
                                    setChapterEdit((current) => ({
                                      ...current,
                                      objectif: event.target.value,
                                    }))
                                  }
                                />
                              </label>
                              <div className="bo-actions bo-span-2">
                                <button
                                  type="button"
                                  className="bo-button bo-button--ghost"
                                  onClick={() => setEditingChapterId(null)}
                                >
                                  Annuler
                                </button>
                                <button className="bo-button bo-button--primary">
                                  Enregistrer
                                </button>
                              </div>
                            </form>
                          ) : (
                            <div className="bo-row bo-row--between">
                              <div>
                                <strong>
                                  {chapter.ordre}. {chapter.titre}
                                </strong>
                                {chapter.objectif && (
                                  <p className="bo-muted">{chapter.objectif}</p>
                                )}
                              </div>
                              {isDraft && (
                                <div className="bo-actions">
                                  <span className="bo-course-drag-handle" role="button"
                                    aria-label={`Déplacer le chapitre ${chapter.titre} par glisser-déposer`}
                                    tabIndex="0" title="Glisser pour réorganiser"
                                    draggable={!reordering}
                                    onDragStart={(event) => startDrag(event, 'chapter', structure.id, chapter.id)}
                                    onDragEnd={() => { draggingRef.current = null; }}
                                  >↕</span>
                                  <button type="button" className="bo-button bo-button--ghost bo-button--compact"
                                    aria-label={`Monter le chapitre ${chapter.titre}`}
                                    disabled={reordering || (structure.chapters || [])[0]?.id === chapter.id}
                                    onClick={() => moveItem('chapter', structure.id, structure.chapters || [], chapter.id, -1)}>↑</button>
                                  <button type="button" className="bo-button bo-button--ghost bo-button--compact"
                                    aria-label={`Descendre le chapitre ${chapter.titre}`}
                                    disabled={reordering || (structure.chapters || []).slice(-1)[0]?.id === chapter.id}
                                    onClick={() => moveItem('chapter', structure.id, structure.chapters || [], chapter.id, 1)}>↓</button>
                                  <button
                                    className="bo-button bo-button--ghost bo-button--compact"
                                    onClick={() => startEditChapter(chapter)}
                                  >
                                    Modifier
                                  </button>
                                  <button
                                    className="bo-button bo-button--danger-ghost bo-button--compact"
                                    onClick={() => deleteChapter(chapter)}
                                  >
                                    Supprimer
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="bo-course-section-list">
                            {(chapter.sections || []).map((section) => (
                              <article
                                className="bo-course-section"
                                key={section.id}
                                onDragOver={(event) => acceptDrag(event, 'section', chapter.id)}
                                onDrop={(event) => dropItem(event, 'section', chapter.id, chapter.sections || [], section.id)}
                              >
                                {editingSectionId === section.id ? (
                                  <form
                                    className="bo-form bo-course-section-editor"
                                    onSubmit={(event) =>
                                      saveSection(event, section)
                                    }
                                  >
                                    {renderSectionFields(
                                      sectionEdit,
                                      (patch) =>
                                        setSectionEdit((current) => ({
                                          ...current,
                                          ...patch,
                                        }))
                                    )}
                                    <div className="bo-actions bo-span-2">
                                      <button
                                        type="button"
                                        className="bo-button bo-button--ghost"
                                        onClick={() =>
                                          setEditingSectionId(null)
                                        }
                                      >
                                        Annuler
                                      </button>
                                      <button className="bo-button bo-button--primary">
                                        Enregistrer la section
                                      </button>
                                    </div>
                                  </form>
                                ) : (
                                  <>
                                    <div className="bo-row bo-row--between">
                                      <div>
                                        <strong>
                                          {section.ordre}. {section.titre}
                                        </strong>
                                        {section.objectif_pedagogique && (
                                          <p className="bo-muted">
                                            {section.objectif_pedagogique}
                                          </p>
                                        )}
                                      </div>
                                      {isDraft && (
                                        <div className="bo-actions">
                                          <span role="button" tabIndex="0"
                                            className="bo-course-drag-handle"
                                            title="Glisser pour réorganiser"
                                            aria-label={`Déplacer la section ${section.titre} par glisser-déposer`}
                                            draggable={!reordering}
                                            onDragStart={(event) => startDrag(event, 'section', chapter.id, section.id)}
                                            onDragEnd={() => { draggingRef.current = null; }}
                                          >↕</span>
                                          <button type="button" className="bo-button bo-button--ghost bo-button--compact"
                                            aria-label={`Monter la section ${section.titre}`}
                                            disabled={reordering || (chapter.sections || [])[0]?.id === section.id}
                                            onClick={() => moveItem('section', chapter.id, chapter.sections || [], section.id, -1)}>↑</button>
                                          <button type="button" className="bo-button bo-button--ghost bo-button--compact"
                                            aria-label={`Descendre la section ${section.titre}`}
                                            disabled={reordering || (chapter.sections || []).slice(-1)[0]?.id === section.id}
                                            onClick={() => moveItem('section', chapter.id, chapter.sections || [], section.id, 1)}>↓</button>
                                          <button
                                            className="bo-button bo-button--ghost bo-button--compact"
                                            onClick={() =>
                                              startEditSection(section)
                                            }
                                          >
                                            Modifier
                                          </button>
                                          <button
                                            className="bo-button bo-button--danger-ghost bo-button--compact"
                                            onClick={() =>
                                              deleteSection(section)
                                            }
                                          >
                                            Supprimer
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    <p className="bo-course-section__excerpt">
                                      {section.contenu}
                                    </p>
                                    <div className="bo-course-section__meta">
                                      {!!section.notions_cles?.length && (
                                        <span>
                                          {section.notions_cles.length} notion(s)
                                          clé(s)
                                        </span>
                                      )}
                                      {!!section.a_retenir?.length && (
                                        <span>
                                          {section.a_retenir.length} point(s) à
                                          retenir
                                        </span>
                                      )}
                                      {!!section.vigilances?.length && (
                                        <span>
                                          {section.vigilances.length} vigilance(s)
                                        </span>
                                      )}
                                      {section.statut_fiabilite && (
                                        <span>
                                          Fiabilité : {section.statut_fiabilite}
                                        </span>
                                      )}
                                    </div>
                                  </>
                                )}
                              </article>
                            ))}
                          </div>

                          {isDraft && creatingSectionFor === chapter.id ? (
                            <form
                              className="bo-form bo-course-section-editor bo-course-create-section"
                              ref={sectionFormRef}
                              onSubmit={(event) =>
                                createSection(event, chapter)
                              }
                            >
                              <div className="bo-panel__header bo-span-2">
                                <h5>Nouvelle section</h5>
                                <button
                                  type="button"
                                  className="bo-button bo-button--ghost bo-button--compact"
                                  onClick={() => setCreatingSectionFor(null)}
                                >
                                  Fermer
                                </button>
                              </div>
                              {renderSectionFields(
                                getSectionDraft(
                                  chapter.id,
                                  (chapter.sections || []).length + 1
                                ),
                                (patch) =>
                                  setSectionDraft(
                                    chapter.id,
                                    patch,
                                    (chapter.sections || []).length + 1
                                  ),
                                { creation: true }
                              )}
                              <div className="bo-actions bo-span-2">
                                <button className="bo-button bo-button--secondary">
                                  Ajouter la section
                                </button>
                              </div>
                            </form>
                          ) : (
                            isDraft && (
                              <button
                                className="bo-button bo-button--ghost bo-course-add-section"
                                onClick={() =>
                                  setCreatingSectionFor(chapter.id)
                                }
                              >
                                + Ajouter une section
                              </button>
                            )
                          )}
                        </article>
                      ))}
                    </div>
                  )}


                </section>
                )}

                <div className="bo-course-meta-grid">
                  <div>
                    <small>Créé</small>
                    <strong>{formatDate(structure.created_at)}</strong>
                  </div>
                  <div>
                    <small>Dernière mise à jour</small>
                    <strong>{formatDate(structure.updated_at)}</strong>
                  </div>
                  <div>
                    <small>Chapitres</small>
                    <strong>{chapterCount}</strong>
                  </div>
                  <div>
                    <small>Sections</small>
                    <strong>{sectionCount}</strong>
                  </div>
                </div>
              </>
            )}

            <button
              className="bo-button bo-button--ghost bo-course-back-button"
              onClick={returnToCatalog}
            >
              Retour au catalogue des cours
            </button>
          </div>
        ) : (
          <div className="bo-empty">Cours introuvable.</div>
        )}
      </section>
      )}
    </div>
  );
}
